/* eslint-disable no-confusing-arrow */
import * as yup from 'yup';

export const bookingStatusInitialValues = {
  // Cost
  flightCost: null,
  hotelCost: null,
  travelInsuranceCost: null,
  airportTransferCost: null,
  visaCost: null,
  // Rebook
  flightRebookCost: null,
  hotelRebookCost: null,
  visaRebookCost: null,
  travelInsuranceRebookCost: null,
  airportTransferRebookCost: null,
  // Quote Cost
  flightQuoteCost: null,
  hotelQuoteCost: null,
  travelInsuranceQuoteCost: null,
  airportTransferQuoteCost: null,
  visaQuoteCost: null,
  // Status
  flightStatus: '',
  hotelStatus: '',
  travelInsuranceStatus: '',
  airportTransferStatus: '',
  visaStatus: '',
  // Comments
  flightComments: '',
  hotelComments: '',
  travelInsuranceComments: '',
  airportTransferComments: '',
  visaComments: '',
  // Refund
  flightCancelRefund: null,
  hotelCancelRefund: null,
  travelCancelInsuranceRefund: null,
  airportCancelTransferRefund: null,
  visaCancelRefund: null,
  // Travel Agency
  visaTravelAgency: '',
  travelInsuranceTravelAgency: '',
  airportTransferTravelAgency: '',
};

const numOptional = () =>
  yup
    .number()
    .transform((v, orig) => (orig === '' || orig === null ? undefined : v))
    .typeError('Must be a number');

export const bookingStatusValidationSchema = yup.object().shape({
  // Required costs (>0) only when that section is enabled
  flightCost: numOptional().when(
    ['$isPendingBooking', '$isFlight'],
    ([isPendingBooking, isFlight], s) =>
      isPendingBooking && isFlight
        ? s
            .moreThan(0, 'Flight cost must be greater than 0')
            .required('Flight cost is required')
        : s,
  ),
  hotelCost: numOptional().when(
    ['$isPendingBooking', '$isHotel'],
    ([isPendingBooking, isHotel], s) =>
      isPendingBooking && isHotel
        ? s
            .moreThan(0, 'Hotel cost must be greater than 0')
            .required('Hotel cost is required')
        : s,
  ),
  travelInsuranceCost: numOptional().when(
    ['$isPendingBooking', '$isTI'],
    ([isPendingBooking, isTI], s) =>
      isPendingBooking && isTI
        ? s
            .moreThan(0, 'Travel insurance cost must be greater than 0')
            .required('Travel insurance cost is required')
        : s,
  ),
  airportTransferCost: numOptional().when(
    ['$isPendingBooking', '$isAT'],
    ([isPendingBooking, isAT], s) =>
      isPendingBooking && isAT
        ? s
            .moreThan(0, 'Airport transfer cost must be greater than 0')
            .required('Airport transfer cost is required')
        : s,
  ),
  visaCost: numOptional().when(
    ['$isPendingBooking', '$isVisa'],
    ([isPendingBooking, isVisa], s) =>
      isPendingBooking && isVisa
        ? s
            .moreThan(0, 'Visa cost must be greater than 0')
            .required('Visa cost is required')
        : s,
  ),

  // Quote costs: optional; if provided (>0). Make required+>0 if you prefer.
  flightQuoteCost: numOptional().when(
    ['$isQuote', '$isFlight'],
    ([isQuote, isFlight], s) =>
      isQuote && isFlight
        ? s
            .moreThan(0, 'Flight quote cost must be greater than 0')
            .required('Flight quote cost is required')
        : s,
  ),
  hotelQuoteCost: numOptional().when(
    ['$isQuote', '$isHotel'],
    ([isQuote, isHotel], s) =>
      isQuote && isHotel
        ? s
            .moreThan(0, 'Hotel quote cost must be greater than 0')
            .required('Hotel quote cost is required')
        : s,
  ),
  travelInsuranceQuoteCost: numOptional().when(
    ['$isQuote', '$isTI'],
    ([isQuote, isTI], s) =>
      isQuote && isTI
        ? s
            .moreThan(0, 'Travel insurance quote cost must be greater than 0')
            .required('Travel insurance quote cost is required')
        : s,
  ),
  airportTransferQuoteCost: numOptional().when(
    ['$isQuote', '$isAT'],
    ([isQuote, isAT], s) =>
      isQuote && isAT
        ? s
            .moreThan(0, 'Airport transfer quote cost must be greater than 0')
            .required('Airport transfer quote cost is required')
        : s,
  ),
  visaQuoteCost: numOptional().when(
    ['$isQuote', '$isVisa'],
    ([isQuote, isVisa], s) =>
      isQuote && isVisa
        ? s
            .moreThan(0, 'Visa quote cost must be greater than 0')
            .required('Visa quote cost is required')
        : s,
  ),

  // Status: required only when section enabled
  flightStatus: yup.string().when('$isFlight', {
    is: true,
    then: s => s.required('Flight status is required'),
    otherwise: s => s.notRequired(),
  }),
  hotelStatus: yup.string().when('$isHotel', {
    is: true,
    then: s => s.required('Hotel status is required'),
    otherwise: s => s.notRequired(),
  }),
  travelInsuranceStatus: yup.string().when('$isTI', {
    is: true,
    then: s => s.required('Travel insurance status is required'),
    otherwise: s => s.notRequired(),
  }),
  airportTransferStatus: yup.string().when('$isAT', {
    is: true,
    then: s => s.required('Airport transfer status is required'),
    otherwise: s => s.notRequired(),
  }),
  visaStatus: yup.string().when('$isVisa', {
    is: true,
    then: s => s.required('Visa status is required'),
    otherwise: s => s.notRequired(),
  }),

  // Comments
  flightComments: yup.string(),
  hotelComments: yup.string(),
  travelInsuranceComments: yup.string(),
  airportTransferComments: yup.string(),
  visaComments: yup.string(),

  // Refunds: only >0 during cancellation
  flightCancelRefund: numOptional().when(
    ['$isCancel', '$isFlight'],
    ([isCancel, isFlight], s) =>
      isCancel && isFlight ? s.required('Flight refund cost is required') : s,
  ),
  hotelCancelRefund: numOptional().when(
    ['$isCancel', '$isHotel'],
    ([isCancel, isHotel], s) =>
      isCancel && isHotel ? s.required('Hotel refund cost is required') : s,
  ),
  travelCancelInsuranceRefund: numOptional().when(
    ['$isCancel', '$isTI'],
    ([isCancel, isTI], s) =>
      isCancel && isTI
        ? s.required('Travel insurance refund cost is required')
        : s,
  ),
  airportCancelTransferRefund: numOptional().when(
    ['$isCancel', '$isAT'],
    ([isCancel, isAT], s) =>
      isCancel && isAT
        ? s.required('Airport transfer refund cost is required')
        : s,
  ),
  visaCancelRefund: numOptional().when(
    ['$isCancel', '$isVisa'],
    ([isCancel, isVisa], s) =>
      isCancel && isVisa ? s.required('Visa refund cost is required') : s,
  ),

  // Rebooking
  flightRebookCost: numOptional().when(
    ['$isRebook', '$isFlight'],
    ([isRebook, isFlight], s) =>
      isRebook && isFlight
        ? s
            .moreThan(0, 'Flight rebook cost must be greater than 0')
            .required('Flight rebook cost is required')
        : s,
  ),
  hotelRebookCost: numOptional().when(
    ['$isRebook', '$isHotel'],
    ([isRebook, isHotel], s) =>
      isRebook && isHotel
        ? s
            .moreThan(0, 'Hotel rebook cost must be greater than 0')
            .required('Hotel rebook cost is required')
        : s,
  ),
  travelInsuranceRebookCost: numOptional().when(
    ['$isRebook', '$isTI'],
    ([isRebook, isTI], s) =>
      isRebook && isTI
        ? s
            .moreThan(0, 'Travel insurance rebook cost must be greater than 0')
            .required('Travel insurance rebook cost is required')
        : s,
  ),
  airportTransferRebookCost: numOptional().when(
    ['$isRebook', '$isAT'],
    ([isRebook, isAT], s) =>
      isRebook && isAT
        ? s
            .moreThan(0, 'Airport transfer rebook cost must be greater than 0')
            .required('Airport transfer rebook cost is required')
        : s,
  ),
  visaRebookCost: numOptional().when(
    ['$isRebook', '$isVisa'],
    ([isRebook, isVisa], s) =>
      isRebook && isVisa
        ? s
            .moreThan(0, 'Visa rebook cost must be greater than 0')
            .required('Visa rebook cost is required')
        : s,
  ),

  // Travel agencies
  visaTravelAgency: yup.string().when('$isVisa', {
    is: true,
    then: s => s.required('Visa travel agency is required'),
    otherwise: s => s.notRequired(),
  }),
  travelInsuranceTravelAgency: yup.string().when('$isTI', {
    is: true,
    then: s => s.required('Travel insurance travel agency is required'),
    otherwise: s => s.notRequired(),
  }),
  airportTransferTravelAgency: yup.string().when('$isAT', {
    is: true,
    then: s => s.required('Airport transfer travel agency is required'),
    otherwise: s => s.notRequired(),
  }),
});

export const toMoney = v => {
  if (v === '' || v == null) return undefined;
  // Allow strings like "1,234.50"
  const n = typeof v === 'string' ? Number(v.replace(/,/g, '')) : Number(v);
  return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
};
